import { useEffect, type MutableRefObject } from 'react';
import { TimecodeChip } from '@/components/ui/TimecodeChip';
import type { DraftContext } from '@/features/coach-review/composerDrafts';
import { FRAME, formatClock } from '@/features/coach-review/clipTrack';
import type { ResourceVideoSession, ResourceVideoValue } from './resourceVideoSession';
import { ResourceVideoStage, type ResourceStagePosition } from './ResourceVideoStage';

type VideoState = Extract<ReturnType<ResourceVideoSession['getState']>, { status:'available' }>;

/** The moment is wherever the playhead stops: no mark button (plan decision 2). */
export function ResourceStageMoment({ session, state, context, editable, active, focus, matchName, position, setFlush }:{
  session:ResourceVideoSession; state:VideoState; context:DraftContext; editable:boolean; active:boolean; focus:boolean;
  matchName:string; position:MutableRefObject<ResourceStagePosition>; setFlush(fn:(() => void) | null):void;
}) {
  const value = state.value as ResourceVideoValue;
  const place = (seconds:number) => {
    const current = session.getState();
    if (current.status !== 'available' || !current.canEdit) return;
    const moment = current.value as ResourceVideoValue;
    if (Math.abs(moment.timestampSeconds - seconds) >= FRAME / 2) session.change({ ...moment, timestampSeconds:seconds });
  };
  // A save while playing takes the frame on screen, not the last pause.
  useEffect(() => () => setFlush(null), [setFlush]);
  return <ResourceVideoStage session={session} state={state} context={context} editable={editable} active={active} focus={focus}
    matchName={matchName} anchor={value.timestampSeconds} position={position}
    onTime={(seconds, video) => { setFlush(() => place(video.currentTime)); place(seconds); }}
    readout={time => <span className="flex items-center gap-2" data-testid="resource-moment-readout">
      <span className="text-xs">This moment</span><TimecodeChip>{formatClock(editable ? time : value.timestampSeconds)}</TimecodeChip>
    </span>}
    footer={() => <p className="editor-waiting" data-testid="resource-moment-hint">
      Scrub or play to the frame. The moment is wherever the playhead stops. Space plays and pauses.</p>}/>;
}
